/**
 * Scheduled Jobs Storage Router
 * When USE_GOBODO_API is true, delegates to Gobodo; otherwise uses scheduledJobsStorage (localStorage).
 */

import type { ScheduledJob } from '../types/scheduledJobs';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { scheduledJobsStorage } from './scheduledJobsStorage';
import * as gobodoService from './gobodo/gobodoScheduledJobService';

const generateId = () => `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export async function getAllJobsAsync(): Promise<ScheduledJob[]> {
  if (USE_GOBODO_API) return gobodoService.getJobs();
  return Promise.resolve(scheduledJobsStorage.getAllJobs());
}

export async function getJobAsync(jobId: string): Promise<ScheduledJob | null> {
  if (USE_GOBODO_API) return gobodoService.getJobById(jobId);
  return Promise.resolve(scheduledJobsStorage.getJob(jobId));
}

export async function createJobAsync(job: Omit<ScheduledJob, 'id'>): Promise<ScheduledJob> {
  if (USE_GOBODO_API) return gobodoService.createJob(job);
  const withId: ScheduledJob = { ...job, id: generateId() };
  return Promise.resolve(scheduledJobsStorage.saveJob(withId));
}

export async function updateJobAsync(jobId: string, updates: Partial<ScheduledJob>): Promise<ScheduledJob | null> {
  if (USE_GOBODO_API) return gobodoService.updateJob(jobId, updates);
  return Promise.resolve(scheduledJobsStorage.updateJob(jobId, updates));
}

export async function deleteJobAsync(jobId: string): Promise<boolean> {
  if (USE_GOBODO_API) return gobodoService.deleteJob(jobId);
  return Promise.resolve(scheduledJobsStorage.deleteJob(jobId));
}

export async function toggleJobStatusAsync(jobId: string): Promise<ScheduledJob | null> {
  if (USE_GOBODO_API) return gobodoService.toggleJobStatus(jobId);
  return Promise.resolve(scheduledJobsStorage.toggleJobStatus(jobId));
}
